

import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { useGuestLogs } from "@/providers/GuestLogs";
import { INVALID_STRING_PATTERN } from "@/lib/utils/default-returner";
import { IconAlertTriangle, IconX, IconPlus } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useState, useRef, useEffect } from "react";

type NoteEntryProps = {
  guestId: string;
};

export const NoteEntry: React.FC<NoteEntryProps> = ({ guestId }) => {
  const { addLog } = useGuestLogs();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const t = useTranslations("guestDetails");
  const tErrors = useTranslations("errors");

  // Add effect to focus the textarea when the component opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (INVALID_STRING_PATTERN.test(note)) {
      setError(tErrors("validationError"));
      return;
    }
    try {
      await addLog(guestId, note);
    } catch (e) {
      // Keep the composer open and the text intact. Clearing it on a rejected
      // write is how a 403 became silent data loss: the box emptied, nothing
      // appeared in the timeline, and the user had every reason to believe the
      // note was saved.
      //
      // The CODE matters, not just the fact of failure: a bare "Saving failed.
      // Please try again." invites a retry that can never succeed when the real
      // answer is `noAccessToResource`.
      setError(translateBackendError(tErrors, e as Error, tErrors("unableToSave")));
      return;
    }
    setNote("");
    setError(null);
    setIsOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
    // Optional: Clear error when user starts typing again
    if (error) setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setNote("");
    setError(null);
  };

  if (!isOpen) {
    return (
      <div className="w-full flex justify-end items-end mb-2">
        <button
          onClick={() => setIsOpen(true)}
          className="rounded-full p-3 border outline-tertiary "
        >
          <IconPlus size={20} className="font-secondary" />
        </button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 relative outline-quaternary">
      <button
        onClick={handleClose}
        className="absolute top-3 right-3 font-tertiary hover:font-secondary"
        aria-label={t("closeNoteEntry")}
      >
        <IconX size={20} strokeWidth={1.5} />
      </button>
      <textarea
        ref={textareaRef}
        className="w-full placeholder:font-tertiary min-h-15 p-2 outline-hidden resize-none border-0 body-2-regular"
        placeholder={t("addNotePlaceholder")}
        value={note}
        onChange={handleChange}
      />
      {error && (
        <div className="font-error body-3-regular mt-1 flex items-center">
          <IconAlertTriangle size={16} className="mr-1" strokeWidth={1.5} />
          {error}
        </div>
      )}
      <div className="flex justify-end mt-2">
        <SecondaryButton
          size="small"
          onClick={async () => {
            setLocalLoading(true);
            try {
              await handleSave();
              setLocalLoading(false);
            } catch {
              setLocalLoading(false);
            }
          }}
          loading={localLoading}
          text={t("saveNote")}
        />
      </div>
    </div>
  );
};
